import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Col, Row, Modal, Card, Menu, Pagination, Popconfirm, Space, Dropdown, Button } from 'antd';
import { useIntl } from 'react-intl';
import { RiArrowLeftLine, RiCloseFill } from 'react-icons/ri';
import { PiPencilSimpleLine, PiTrashSimple } from 'react-icons/pi';
import { MdOutlineUnpublished } from 'react-icons/md';
import { DownOutlined } from '@ant-design/icons';
import IntlMessages from '../../../../../layout/components/lang/IntlMessages';
import urlPageCategories from '../../../../../urls/urlPageCategories';
import urlPageCategoryEdit from '../../../../../urls/urlPageCategoryEdit';
import { EMPTY_VALUE_PLACEHOLDER } from '../../../../../const/system';
import { TYPE_DICTIONARY } from '../../../../Template builder/const/templates';
import { Permissions } from '../../../../../const/permissions';
import { HasRights, PrivateComponent } from '../../../../../components/HasRights/HasRights';
import mkPreparedTemplateData from '../../../utils/mkPreparedTemplateData';
import useQueryParams from '../../../../../app/hooks/useQueryParams';
import useToggle from '../../../../../app/hooks/useToggle';
import handleResponse from '../../../../../lib/handleResponse';
import Spinner from '../../../../../components/Spinner';
import BreadCrumbs from '../../../../../layout/components/breadcrumbs';
import ActionButton from '../../../../../layout/components/action-button';
import ContentCard from '../../../components/ContentCard';
import CategoryDetailsCard from '../../../components/CategoryDetailsCard';
import TotalLabel from '../../../../../components/TotalLabel';
import ImageModalCardBodyView from '../../../components/ImageModalCard/ImageModalCardBodyView';
import EmptyDataPlaceholder from '../../../../../components/EmptyDataPlaceholder';
import { useGetImagesQuery } from '../../../api/imagesApiSlice';
import { useGetDictionaryQuery } from '../../../../Translations/api/dictionariesApiSlice';
import { useGetLanguagesQuery } from '../../../../Translations/api/languagesApiSlice';
import {
  useDeleteCategoryMutation,
  useGetCategoryQuery,
  useUnpublishCategoryMutation,
} from '../../../api/categoriesApiSlice';



const PageCategoryDetails = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [ previewModalOpen, togglePreviewModal ] = useToggle();
  const [ previewImage, setPreviewImage ] = useState({});

  const indexPageSearchParams = localStorage.getItem('indexSearchParams');

  const {
    pagination: pagination,
    searchParams,
    handleChangeTableParams,
  } = useQueryParams({
    paginationInit: { page: 1, limit: 12 } });

  const { data: categoryDetails = {}, isFetching: isCategoryFetching } = useGetCategoryQuery({
    categoryId,
    includes: '?include=dynamic_data,template,target',
  });

  const { data: {
    data: images = [],
    pagination: dataPagination = {},
  } = { data: [], pagination: {} }, isFetching: isImagesFetching } = useGetImagesQuery({
    queryParams: `${searchParams.toString()}&search=category:${categoryId}`,
  });

  const dictionaryField = categoryDetails?.dynamic_data?.data?.find((item) => item.type === TYPE_DICTIONARY);
  const isPublished = Boolean(categoryDetails?.is_published);

  const { data: dictionary = {} } = useGetDictionaryQuery({
    id: dictionaryField?.value,
    include: 'translations,structure,structure.fields.translations',
  }, { skip: !dictionaryField?.value });

  const { data: {
    data: languages = [],
  } = { data: [] } } = useGetLanguagesQuery({ queryParams: 'limit=0' }, { skip: !dictionaryField });

  const [ deleteCategory, { isLoading: isCategoryDeleting } ] = useDeleteCategoryMutation();
  const [ unpublishCategory, { isLoading: isUnpublishCategoryLoading } ] = useUnpublishCategoryMutation();

  const data = mkPreparedTemplateData({
    templateData: categoryDetails?.template?.data?.fields?.data,
    dynamicData: categoryDetails?.dynamic_data?.data,
    dictionary,
    languages,
  });

  const isLoadingData = isCategoryFetching || isCategoryDeleting || isUnpublishCategoryLoading;


  const handleUnpublish = () => {
    unpublishCategory(categoryId)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-unpublished-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-unpublish-failed', [], error));
  };

  const handleDelete = () => {
    deleteCategory(categoryId)
      .unwrap()
      .then(() => {
        handleResponse(
          'success',
          intl,
          'ui-general-deleted-successfully',
          [ () => navigate(urlPageCategories()) ],
        );
      })
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const handleCardClick = (item) => {
    setPreviewImage(item);
    togglePreviewModal();
  };

  const handlePreviewModalClose = () => {
    togglePreviewModal();
  };

  const showTotal = (total) => <TotalLabel total={total} />;


  const allowRenderActions = HasRights([ Permissions.CONTENT.CATEGORIES.EDIT ]) ||
    (isPublished && HasRights([ Permissions.CONTENT.CATEGORIES.UNPUBLISH ])) ||
    (!isPublished && HasRights([ Permissions.CONTENT.CATEGORIES.DELETE ]));

  const actionItems = (
    <Menu>
      <PrivateComponent allowedPermissions={[ Permissions.CONTENT.CATEGORIES.EDIT ]}>
        <Menu.Item
          key="edit"
          icon={<PiPencilSimpleLine size={18} />}
          onClick={() => navigate(urlPageCategoryEdit({ categoryId }))}
        >
          <IntlMessages id="ui-general-edit" />
        </Menu.Item>
      </PrivateComponent>
      {isPublished ? (
        <PrivateComponent allowedPermissions={[ Permissions.CONTENT.CATEGORIES.UNPUBLISH ]}>
          <Menu.Item
            key="unpublish"
            icon={<MdOutlineUnpublished size={18} />}
            onClick={handleUnpublish}
          >
            <IntlMessages id="ui-general-unpublish" />
          </Menu.Item>
        </PrivateComponent>
      ) : (
        <PrivateComponent allowedPermissions={[ Permissions.CONTENT.CATEGORIES.DELETE ]}>
          <Popconfirm
            title={
              <>
                <div><IntlMessages id="ui-general-delete-confirm-message" /></div>
                <IntlMessages id="categories-delete-notification" />
              </>
            }
            placement="topLeft"
            okButtonProps={{ danger: true }}
            onConfirm={handleDelete}
          >
            <Menu.Item
              key="delete"
              icon={<PiTrashSimple size={18} />}
              onClick={() => {}}
            >
              <IntlMessages id="ui-general-delete" />
            </Menu.Item>
          </Popconfirm>
        </PrivateComponent>
      )}
    </Menu>
  );

  return (
    <>
      <Modal
        width={1200}
        title={intl.formatMessage({ id: 'content-image-preview-card-title' })}
        centered
        visible={previewModalOpen}
        onCancel={handlePreviewModalClose}
        footer={false}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <ImageModalCardBodyView data={previewImage} />
      </Modal>

      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPageCategories({ indexPageSearchParams })}>
              <IntlMessages id='categories-breadcrumbs' />
            </Link>
          }
          breadCrumbActive={categoryDetails?.id ?? EMPTY_VALUE_PLACEHOLDER}
        />

        <Col md={10} span={24} className='text-end'>
          <Space size='middle'>
            {allowRenderActions && (
              <Dropdown overlay={actionItems}>
                <Button onClick={(ev) => ev.preventDefault()}>
                  <Space size='middle'>
                    <IntlMessages id='ui-general-action' />
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            )}

            <ActionButton
              icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
              onClick={() => navigate(urlPageCategories())}
              title={<IntlMessages id='ui-general-back-to-list' />}
            />
          </Space>
        </Col>
      </Row>

      <Spinner spinning={isLoadingData}>
        <Row gutter={[ 32, 32 ]} className="hp-mb-32">
          <Col span={24}>
            <CategoryDetailsCard
              data={categoryDetails}
              isLoadingCard={isUnpublishCategoryLoading || isCategoryDeleting}
              isFetching={isCategoryFetching}
              dictionary={dictionary}
              languages={languages}
              templateCardsData={data}
            />
          </Col>
        </Row>

        <Col span={24}>
          <Card
            id="attached-images"
            loading={isImagesFetching}
            title={<IntlMessages id="categories-view-images" />}
          >
            {images?.length ? (
              <Row gutter={[ 16, 32 ]}>
                {images?.map((item) => {
                  return (
                    <Col className='gutter-row' key={item.id} xs={24} sm={12} md={8} lg={6} xxl={4}>
                      <ContentCard
                        key={item.id}
                        data={item}
                        onClick={handleCardClick}
                      />
                    </Col>
                  );
                })}
                <Col span={24}>
                  {dataPagination.total && (
                    <Pagination
                      total={dataPagination.total}
                      showTotal={showTotal}
                      pageSize={pagination.limit}
                      current={pagination.page}
                      pageSizeOptions={[ '12', '24', '48', '96' ]}
                      showSizeChanger
                      onChange={(page, size) => {
                        handleChangeTableParams({
                          current: page,
                          pageSize: size,
                        }, {}, {});
                      }}
                    />
                  )}
                </Col>
              </Row>
            ) : <EmptyDataPlaceholder placeholder={<IntlMessages id="categories-view-images-empty" />} />}
          </Card>
        </Col>
      </Spinner>
    </>
  );
};

export default PageCategoryDetails;
